import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Tabular export helper — CSV / XLSX download that works in the browser
 * and on Capacitor native platforms.
 *
 * On the web a Blob is handed to `file-saver`. On native the file is
 * written to Documents (Cache fallback) and offered through the OS
 * share sheet, since Capacitor WebViews cannot trigger downloads.
 *
 * columns: [{ key: 'reference', label: 'Reference' }, …]
 * rows:    array of objects; missing keys render as empty cells.
 */

const sanitize = (name) => name.replace(/[^A-Za-z0-9._-]+/g, '_');

function toAoa(rows, columns) {
  const labels = columns.map((c) => c.label);
  const body = rows.map((row) => columns.map((c) => {
    const v = row[c.key];
    return v === undefined || v === null ? '' : v;
  }));
  return [labels, ...body];
}

function toCsv(rows, columns) {
  const escapeCell = (v) => {
    const s = String(v);
    return /[",\r\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = toAoa(rows, columns).map((cells) => cells.map(escapeCell).join(','));
  // BOM keeps Excel happy with accented characters.
  return `\uFEFF${lines.join('\r\n')}`;
}

async function writeNative(filename, data, isBinary) {
  const options = {
    path: filename,
    data,
    directory: Directory.Documents,
    ...(isBinary ? {} : { encoding: Encoding.UTF8 }),
  };
  try {
    await Filesystem.checkPermissions();
  } catch (_) { /* plugin may not expose permissions — ignore */ }
  const result = await Filesystem.writeFile(options);
  const uriResult = result.uri
    ? { uri: result.uri }
    : await Filesystem.getUri({ path: filename, directory: Directory.Documents });
  await Share.share({ title: filename, url: uriResult.uri });
  return uriResult.uri;
}

/**
 * @param {Object}   opts
 * @param {Array}    opts.rows      data rows (objects)
 * @param {Array}    opts.columns   [{ key, label }]
 * @param {'csv'|'xlsx'} [opts.format]
 * @param {string}   opts.filename  e.g. avocash-transactions-campaign_12_2026-01.csv
 * @param {string}   [opts.sheetName] first-sheet title for XLSX
 * @returns {Promise<{filename: string}>}
 */
export async function exportRows({ rows, columns, format = 'csv', filename, sheetName = 'Transactions' }) {
  if (!rows || !rows.length) {
    throw new Error('exportRows: nothing to export');
  }
  const safeName = sanitize(filename);

  if (Capacitor.isNativePlatform()) {
    if (format === 'xlsx') {
      const sheet = XLSX.utils.aoa_to_sheet(toAoa(rows, columns));
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, sheetName.slice(0, 31) || 'Sheet1');
      const base64 = XLSX.write(book, { bookType: 'xlsx', type: 'base64' });
      await writeNative(safeName, base64, true);
    } else {
      await writeNative(safeName, toCsv(rows, columns), false);
    }
    return { filename: safeName };
  }

  let blob;
  if (format === 'xlsx') {
    const sheet = XLSX.utils.aoa_to_sheet(toAoa(rows, columns));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, sheetName.slice(0, 31) || 'Sheet1');
    const out = XLSX.write(book, { bookType: 'xlsx', type: 'array' });
    blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  } else {
    blob = new Blob([toCsv(rows, columns)], { type: 'text/csv;charset=utf-8' });
  }
  saveAs(blob, safeName);
  return { filename: safeName };
}

export default { exportRows };
